import React, { useState, useEffect, Fragment, memo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TextField,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import { useSelector } from "react-redux";
import FilterListIcon from "@mui/icons-material/FilterList";

import CustomPagination from "../../components/PotatoProtection/LargeScreen/Landing/CustomPagination";
import useThemeRender from "../../theme/useThemeRender";
import { darkTheme, lightTheme } from "../../theme/Theme";
import { tableCellStyle, headerTextStyle, dividerStyle } from "./tableStyle";

const ColumnHeader = ({ columns, isDarkTheme }) => (
  <TableRow>
    {columns.map((col) => (
      <TableCell
        key={col.id}
        sx={{
          width: col.width || "auto",
          color: isDarkTheme
            ? darkTheme.textColorLayer4
            : lightTheme.textColorLayer4,
          ...tableCellStyle,
        }}
      >
        <Typography
          sx={{
            ...headerTextStyle,
            color: isDarkTheme
              ? darkTheme.textColorLayer4
              : lightTheme.textColorLayer4,
            width: col.headerWidth || "65%",
          }}
        >
          {col.label}
        </Typography>
      </TableCell>
    ))}
  </TableRow>
);

const ColumnFilters = ({ columns, columnSearch, onChange, isDarkTheme }) => (
  <TableRow>
    {columns.map((col) => (
      <TableCell key={col.id} sx={{ padding: 0 }}>
        {col.filterable && (
          <>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={columnSearch[col.id]}
              onChange={(e) => onChange(col.id, e.target.value)}
              placeholder="User Input"
              sx={{
                width: "70%",
                mb: 2,
                "& .MuiInputBase-root": {
                  height: "24px",
                  border: isDarkTheme ? "1px solid white" : "",
                },
                "& .MuiOutlinedInput-input": {
                  px: 1,
                  py: 0.5,
                  color: isDarkTheme ? "white" : "black",
                },
              }}
            />
            <FilterListIcon
              sx={{
                ml: 1,
                width: 16,
                height: 16,
                fill: isDarkTheme ? "white" : "black",
              }}
            />
          </>
        )}
      </TableCell>
    ))}
  </TableRow>
);

const TableRowContent = ({
  row,
  columns,
  isDarkTheme,
  onRowClick,
  renderActions,
  cellRenderer,
  expandedRow,
  renderChildComponent,
}) => (
  <Fragment key={row.id}>
    <TableRow
      onClick={() => onRowClick(row)}
      sx={{
        cursor: "pointer",
        boxShadow: "0px 2px 0px 0px var(--color-border-default) inset",
        borderBottom: "2px solid var(--color-border-default)",
        "&:hover": {
          backgroundColor: isDarkTheme ? "rgba(255, 255, 255, 0.1)" : "#f5f5f5",
        },
        "&:nth-of-type(odd)": {
          color: isDarkTheme
            ? darkTheme.textColorLayer4
            : lightTheme.textColorLayer4,
        },
      }}
    >
      {columns.map((col) => (
        <TableCell
          key={col.id}
          sx={{
            border: "none",
            color: isDarkTheme
              ? darkTheme.textColorLayer4
              : lightTheme.textColorLayer4,
            fontFamily: "Inter",
            fontWeight: 400,
            fontSize: "14px",
          }}
        >
          {col.cellStyle === "cellStyle"
            ? cellRenderer
              ? cellRenderer(row[col.id], row)
              : row[col.id]
            : col.id === "actions" && renderActions
            ? renderActions(row)
            : row[col.id]}
        </TableCell>
      ))}
    </TableRow>

    {renderChildComponent && (
      <TableRow>
        <TableCell colSpan={columns.length} sx={{ p: 0, border: "none" }}>
          <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit>
            <Box>{renderChildComponent(row)}</Box>
          </Collapse>
        </TableCell>
      </TableRow>
    )}
  </Fragment>
);

const NoRecordsRow = ({ columns, isDarkTheme }) => (
  <TableRow>
    <TableCell
      colSpan={columns.length}
      sx={{
        textAlign: "center",
        color: isDarkTheme ? "white" : "black",
        fontWeight: "bold",
        py: 2,
      }}
    >
      No Records Found
    </TableCell>
  </TableRow>
);

const DynamicTable = memo(
  ({
    columns,
    rows,
    searchText,
    onRowClick,
    rowsPerPage = 10,
    showColumnFilters = true,
    renderActions,
    expandedRow,
    renderChildComponent,
    cellRenderer,
  }) => {
    const isDarkTheme = useSelector((state) => state.theme);
    const [page, setPage] = useState(0);
    const isMobile = useMediaQuery("(max-width:800px)");
    const [columnSearch, setColumnSearch] = useState(
      columns.reduce((acc, col) => ({ ...acc, [col.id]: "" }), {})
    );

    useEffect(() => setPage(0), [searchText, columnSearch]);

    const handleColumnSearchChange = (colId, value) => {
      setColumnSearch((prev) => ({ ...prev, [colId]: value }));
    };

    const filteredRows = rows?.filter((row) => {
      const matchesGlobal = Object.values(row).some((val) =>
        String(val || "")
          .toLowerCase()
          .includes(searchText?.toLowerCase())
      );
      const matchesColumn = columns.every((col) => {
        const colVal = String(row[col.id] || "").toLowerCase();
        const filter = columnSearch[col.id]?.toLowerCase();
        return filter === "" || colVal.includes(filter);
      });
      return matchesGlobal && matchesColumn;
    });

    const paginatedRows = filteredRows.slice(
      page * rowsPerPage,
      (page + 1) * rowsPerPage
    );

    return (
      <TableContainer>
        <Table
          sx={{
            borderCollapse: "collapse",
            backgroundColor: useThemeRender("cardBackgroundColorLayer1"),
          }}
        >
          <TableHead>
            <ColumnHeader columns={columns} isDarkTheme={isDarkTheme} />
            {showColumnFilters && !isMobile && (
              <ColumnFilters
                columns={columns}
                columnSearch={columnSearch}
                onChange={handleColumnSearchChange}
                isDarkTheme={isDarkTheme}
              />
            )}
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ p: 0, border: "none" }}>
                <Box sx={dividerStyle} />
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.length > 0 ? (
              paginatedRows.map((row) => (
                <TableRowContent
                  key={row.id}
                  row={row}
                  columns={columns}
                  isDarkTheme={isDarkTheme}
                  onRowClick={onRowClick}
                  renderActions={renderActions}
                  cellRenderer={cellRenderer}
                  expandedRow={expandedRow}
                  renderChildComponent={renderChildComponent}
                />
              ))
            ) : (
              <NoRecordsRow columns={columns} isDarkTheme={isDarkTheme} />
            )}
          </TableBody>
        </Table>

        {filteredRows?.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", pb: 2 }}>
            <CustomPagination
              page={page}
              setPage={setPage}
              totalPages={Math.ceil(filteredRows.length / rowsPerPage)}
            />
          </Box>
        )}
      </TableContainer>
    );
  },
  (prev, next) =>
    prev.columns === next.columns &&
    prev.searchText === next.searchText &&
    prev.expandedRow === next.expandedRow
);

export default DynamicTable;
